import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Download } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty';
import { DataGrid } from '@/components/data-grid';
import type { ReportTemplate } from './ReportCard';

/** Drawer tab (legacy; panel now shows History only). */
export type DrawerTab = 'config' | 'runs';

/** Row shape for execution history DataGrid (no callbacks in data). */
export interface ReportExecutionHistoryRow {
  id: string;
  name: string;
  date: string;
  dateRaw: string;
  status: string;
  downloading: boolean;
}

const columnHelper = createColumnHelper<ReportExecutionHistoryRow>();

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const s = status?.toLowerCase() ?? '';
  if (s === 'completed') return 'default';
  if (s === 'failed') return 'destructive';
  if (s === 'running' || s === 'queued') return 'secondary';
  return 'outline';
}

function ExecutionHistoryGrid({
  data,
  selectedExecutionId,
  onExecutionSelect,
  onExecutionDownload,
}: {
  data: ReportExecutionHistoryRow[];
  selectedExecutionId?: string | null;
  onExecutionSelect: (id: string) => void;
  onExecutionDownload: (id: string) => void;
}) {
  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Report',
        cell: ({ getValue }) => (
          <span className="text-nb-text-primary whitespace-nowrap">{getValue()}</span>
        ),
        size: 200,
        minSize: 180,
      }),
      columnHelper.accessor((row) => row.dateRaw, {
        id: 'date',
        header: 'Run date',
        cell: ({ row }) => (
          <span className="text-xs text-nb-text-muted whitespace-nowrap" title={row.original.date}>
            {row.original.date}
          </span>
        ),
        size: 200,
        minSize: 180,
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue();
          const label = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Unknown';
          return <Badge variant={statusVariant(status)}>{label}</Badge>;
        },
        size: 100,
        minSize: 72,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onExecutionDownload(row.original.id);
            }}
            disabled={row.original.downloading}
            className="h-7 px-2 text-xs"
          >
            <Download size={12} className="mr-1" />
            {row.original.downloading ? 'Preparing...' : 'Download'}
          </Button>
        ),
        size: 100,
        minSize: 80,
      }),
    ],
    [onExecutionDownload]
  );

  return (
    <DataGrid<ReportExecutionHistoryRow>
      data={data}
      columns={columns}
      getRowId={(row) => row.id}
      density="comfortable"
      enableQuickFilter
      quickFilterPlaceholder="Search executions..."
      enableRowSelection={false}
      enableColumnResize
      enableColumnPinning={false}
      enableSorting
      enableCopy={false}
      enableCsvExport={false}
      emptyMessage="No executions"
      onRowClick={(row) => onExecutionSelect(row.original.id)}
      getRowClassName={(row) =>
        selectedExecutionId === row.original.id ? 'bg-nb-accent/15 border-l-2 border-l-nb-accent' : ''
      }
      className="border-0"
      scrollContainerClassName="flex-1 min-h-[200px]"
    />
  );
}

interface ReportConfigPanelProps {
  selectedReport: ReportTemplate | null;
  selectedProject: { id: string | number; name: string } | null;
  drawerTab: DrawerTab;
  onDrawerTabChange: (tab: DrawerTab) => void;
  onClose: () => void;

  // Form state
  fullSchedule: boolean;
  onFullScheduleChange: (value: boolean) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  highFloatThresholdDays?: string;
  onHighFloatThresholdDaysChange?: (value: string) => void;
  lowFloatThresholdDays?: string;
  onLowFloatThresholdDaysChange?: (value: string) => void;
  groupBy: string;
  onGroupByChange: (value: string) => void;
  showAnalysisSections: boolean;
  onShowAnalysisSectionsChange: (value: boolean) => void;

  // Actions
  onGenerate: () => void;
  onDownload: () => void;
  generatingReport: boolean;
  error: string | null;
  onErrorRetry?: () => void;
  onErrorReset?: () => void;
  onErrorDownloadLastGood?: () => void;
  lastGoodPdfAvailable?: boolean;
  currentExecutionId?: string;

  // Runs tab (DataGrid)
  historyItems: ReportExecutionHistoryRow[];
  selectedExecutionId?: string | null;
  onExecutionSelect: (id: string) => void;
  onExecutionDownload: (id: string) => void;

  /** 'drawer' = fixed overlay with backdrop (default); 'inline' = in-flow column, no backdrop. */
  variant?: 'drawer' | 'inline';
}

export const ReportConfigPanel: React.FC<ReportConfigPanelProps> = ({
  selectedReport,
  onClose,
  fullSchedule,
  onFullScheduleChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  highFloatThresholdDays = '',
  onHighFloatThresholdDaysChange,
  lowFloatThresholdDays = '',
  onLowFloatThresholdDaysChange,
  historyItems,
  selectedExecutionId,
  onExecutionSelect,
  onExecutionDownload,
  variant = 'drawer',
}) => {
  if (!selectedReport) return null;

  const supportsDateWindow = selectedReport.supported_context?.date_window ?? false;
  const isHighFloatLowFloat = selectedReport.id === 'high-float-low-float:v1';

  const panelContent = (
    <>
        {/* Header */}
        <div className="flex-none px-6 py-4 border-b border-nb-border-subtle flex items-center justify-between bg-nb-surface-1">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-nb-text-primary truncate">
              {selectedReport.name}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close panel"
            className="ml-3 flex-shrink-0 text-nb-text-muted hover:text-nb-text-primary hover:bg-nb-surface-2 h-8 w-8 p-0 rounded-md"
          >
            <X size={18} />
          </Button>
        </div>

        {/* Report options: date window and/or high/low float thresholds */}
        {(supportsDateWindow || isHighFloatLowFloat) && (
          <div className="flex-none px-6 py-4 border-b border-nb-border-subtle space-y-4 bg-nb-surface-0">
            <h3 className="text-sm font-medium text-nb-text-primary">Report options</h3>
            {supportsDateWindow && (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-nb-text-secondary cursor-pointer">
                  <Checkbox
                    checked={fullSchedule}
                    onCheckedChange={(c) => onFullScheduleChange(c === true)}
                  />
                  Full schedule (ignore date range)
                </label>
                {!fullSchedule && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-nb-text-muted">Window start</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => onStartDateChange(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-nb-text-muted">Window end</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => onEndDateChange(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {isHighFloatLowFloat && onHighFloatThresholdDaysChange != null && onLowFloatThresholdDaysChange != null && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-nb-text-muted">High float threshold (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="e.g. 20"
                    value={highFloatThresholdDays}
                    onChange={(e) => onHighFloatThresholdDaysChange(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-nb-text-muted">Low float threshold (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="e.g. 5"
                    value={lowFloatThresholdDays}
                    onChange={(e) => onLowFloatThresholdDaysChange(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* History */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {historyItems.length === 0 ? (
            <div className="flex items-center justify-center flex-1 px-6 py-4">
              <EmptyState
                description="No runs yet. Run a report from the table to see it here."
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col px-6 py-4">
              <ExecutionHistoryGrid
                data={historyItems}
                selectedExecutionId={selectedExecutionId ?? undefined}
                onExecutionSelect={onExecutionSelect}
                onExecutionDownload={onExecutionDownload}
              />
            </div>
          )}
        </div>
    </>
  );

  if (variant === 'inline') {
    return (
      <div
        className="w-[420px] flex-shrink-0 flex flex-col h-full min-h-0 bg-nb-surface-1 border-l border-nb-border-subtle overflow-hidden"
        data-testid="report-config-panel-inline"
      >
        {panelContent}
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed right-0 top-0 h-full w-[min(700px,92vw)] z-50 flex flex-col shadow-2xl bg-nb-surface-1 border-l border-nb-border-subtle animate-in slide-in-from-right duration-300"
        data-testid="report-config-panel-drawer"
      >
        {panelContent}
      </aside>
    </>
  );
}
